import { SelectedOptionGroups } from '@services/database/mco/schema/public';
import axios from 'axios';
import { resetProductColorPrice } from '@modules/ProductColorPrice/S/fs';
import { makeApiUrl } from '@services/MarppleShop/shared/util';
import { BaseProductAdditionalPriceRateS } from '@modules/BaseProduct/AdditionalPriceRate/S/Function/module/BaseProductAdditionalPriceRateS';

type ProductPriceInfoInput = {
  id: number;
  base_product_color_id: number;
  base_product_size_id: number;
  base_product_id: number;
  product_faces2: object;
};

type ProductPriceInfoOutput = {
  price_info?: object;
  price?: number;
  price_en?: number;
  price_jp?: number;
};

type PriceMaterial = {
  base_product_additional_price_rate: object;
  bp_discount_set: object;
  base_product: object;
  base_product_color: object;
};

// 간단 캐시 저장소
const cache = new Map<string, any>();

/**
 * 캐시 가능한 axios GET
 * @param url 요청할 URL
 * @param config axios 옵션
 * @param ttlMs 캐시 유지 시간(ms), 기본 1분
 */
async function cachedGet<T = any>(
  { id, base_product_color_id }: { id: number; base_product_color_id: number },
  ttlMs: number = 60000,
): Promise<T> {
  const key = id + '_' + base_product_color_id;
  const cached = cache.get(key);
  if (cached && Date.now() < cached.expire) {
    return cached.data;
  }

  const res = await axios.get(
    makeApiUrl(`/${T.lang}/@api/products/:product_id/price_set`, {
      product_id: id,
    }),
    {
      params: {
        product_id: id,
        base_product_color_id: base_product_color_id,
      },
    },
  );
  cache.set(key, { data: res.data, expire: Date.now() + ttlMs });
  return res.data;
}

const makeProductPrice = async (
  product: ProductPriceInfoInput,
  selected_option_group: SelectedOptionGroups,
): Promise<ProductPriceInfoOutput | undefined> => {
  const { success, data: product_material } = await cachedGet<{ success: boolean; data: PriceMaterial }>(
    product,
  );
  if (!success) {
    return;
  }
  const product_for_calc_price = {
    base_product_id: product.base_product_id,
    base_product_size_id: product.base_product_size_id,
    base_product_color_id: product.base_product_color_id,
    product_faces2: product.product_faces2,
    price_info: undefined,
    price: undefined,
    price_en: undefined,
    price_jp: undefined,
    _: {
      selected_option_group,
      base_product_additional_price_rate: product_material.base_product_additional_price_rate,
      bp_discount_set: product_material.bp_discount_set,
      base_product: product_material.base_product,
      base_product_color: product_material.base_product_color,
    },
  };
  resetProductColorPrice(product_for_calc_price);
  const { price_info, price, price_en, price_jp }: ProductPriceInfoOutput = product_for_calc_price;
  return { price_info, price, price_en, price_jp };
};

export const setProductPriceInfo = async (
  product: ProductPriceInfoInput,
  selected_option_group: SelectedOptionGroups,
) => {
  const price_set = await makeProductPrice(product, selected_option_group);
  if (price_set) {
    Object.assign(product, price_set);
    BaseProductAdditionalPriceRateS.setProfit(product);
  }
};
